import type { LeadCommunity, LeadNetworkResponse } from '@/api/types';
import { Callout } from './Callout';
import { LeadNetworkGraph } from './LeadNetworkGraph';
import { StatTile } from './StatTile';
import { formatDuration, formatMoney, formatPercent } from './format';

/**
 * The network view: the graph, the clusters it found, and the leads that hold clusters together.
 *
 * <b>What this answers that a breakdown cannot.</b> A campaign table says how many leads each
 * campaign produced. It cannot say "these 23 leads are one audience arriving through four
 * campaigns", because that fact is not in any single column — it is in the overlap between them.
 *
 * The cluster table is ordered by size but carries won/value on every row, for the same reason the
 * breakdown tables do: the biggest cohort and the one worth having are frequently not the same.
 */
export function LeadNetworkPanel({
  data, isLoading, error, currency, onOpenLead,
}: {
  data: LeadNetworkResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  currency: string | null;
  onOpenLead: (id: string) => void;
}) {
  if (isLoading) {
    return <div className="empty-state"><span className="spinner" /></div>;
  }

  if (error) {
    return <Callout tone="error">Could not build the network. {error.message}</Callout>;
  }

  if (!data || data.leads_considered === 0) {
    return (
      <div className="empty-state t-body">
        No leads in this range to compare.
        <div className="t-label subtle" style={{ marginTop: 4 }}>
          Widen the date range, or clear the filters above.
        </div>
      </div>
    );
  }

  const bridges = data.nodes.filter((node) => node.is_bridge)
    .sort((a, b) => b.betweenness - a.betweenness)
    .slice(0, 6);

  const matchedShare = data.leads_considered > 0 ? data.leads_matched / data.leads_considered : null;

  return (
    <>
      {data.truncated && (
        <Callout tone="warning">
          This range has more leads than one graph can lay out — the analysis below covers the most
          recent {data.leads_considered.toLocaleString('en-US')}. Narrow the range to include the rest.
        </Callout>
      )}

      <div className="kpi-row">
        <StatTile label="Leads in graph" value={data.leads_considered} sub={`${data.edges.length.toLocaleString('en-US')} ties drawn`} />
        <StatTile
          label="Clusters found"
          value={data.communities.length}
          sub={data.communities.length === 0 ? 'nothing resembles anything' : 'by shared attributes'}
        />
        <StatTile
          label="In a cluster"
          value={formatPercent(matchedShare)}
          sub={`${data.isolated_count.toLocaleString('en-US')} stand alone`}
        />
        <StatTile
          label="Bridges"
          value={bridges.length}
          sub="leads linking two clusters"
        />
        <StatTile
          label="Density"
          value={data.density.toFixed(3)}
          // Density is the honesty check on the whole picture, so it says what its own value means.
          sub={data.density > 0.5 ? 'very dense — clusters may be arbitrary' : 'sparse enough to separate'}
        />
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Similarity graph</h2>
          <span className="t-label subtle">
            leads that share campaigns, pages, company domains or geography
          </span>
        </div>
        <div className="card-body">
          <LeadNetworkGraph data={data} onSelect={onOpenLead} />
          {data.edges_omitted > 0 && (
            <div className="t-caption subtle" style={{ marginTop: 8 }}>
              {data.edges_omitted.toLocaleString('en-US')} weaker ties are omitted from the drawing —
              the clusters and rankings above were computed on the complete graph.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Clusters</h2>
          <span className="t-label subtle">what each group has in common, and what it was worth</span>
        </div>

        {data.communities.length === 0 ? (
          <div className="empty-state t-body">
            No cluster reached the similarity threshold — these leads arrived independently.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Cluster</th>
                  <th>Defined by</th>
                  <th className="num">Leads</th>
                  <th className="num">Won</th>
                  <th className="num">Value</th>
                  <th className="num">First reply</th>
                  <th className="num">Cohesion</th>
                </tr>
              </thead>
              <tbody>
                {data.communities.map((community) => (
                  <ClusterRow key={community.id} community={community} currency={currency} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {bridges.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>Bridges</h2>
            <span className="t-label subtle">highest betweenness — they connect otherwise separate groups</span>
          </div>
          <div className="card-body">
            <p className="t-caption subtle" style={{ marginTop: 0 }}>
              These leads sit on the shortest paths between clusters: a visitor who shares a campaign
              with one audience and a company domain with another. Where a campaign is leaking into a
              segment it was not aimed at, it shows up here first.
            </p>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Lead</th>
                    <th>Cluster</th>
                    <th>Campaign</th>
                    <th className="num">Betweenness</th>
                    <th className="num">Ties</th>
                  </tr>
                </thead>
                <tbody>
                  {bridges.map((node) => (
                    <tr key={node.id} className="row-clickable" onClick={() => onOpenLead(node.id)}>
                      <td>{node.label}</td>
                      <td>{node.community >= 0 ? `Cluster ${node.community + 1}` : <span className="subtle">unclustered</span>}</td>
                      <td>{node.campaign ?? <span className="subtle">—</span>}</td>
                      <td className="num tabular">{node.betweenness.toFixed(3)}</td>
                      <td className="num tabular">{node.degree.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ClusterRow({ community, currency }: { community: LeadCommunity; currency: string | null }) {
  return (
    <tr>
      <td>Cluster {community.id + 1}</td>
      <td className="t-caption">
        {community.traits.length === 0
          ? <span className="subtle">no single dominant trait</span>
          : community.traits.map((trait) => (
            <span key={`${trait.dimension}-${trait.value}`} className="trait-chip">
              {trait.dimension}: {trait.value}
              <span className="subtle"> {Math.round(trait.share * 100)}%</span>
            </span>
          ))}
      </td>
      <td className="num">{community.size.toLocaleString('en-US')}</td>
      <td className="num">
        {community.won > 0 ? community.won.toLocaleString('en-US') : <span className="subtle">—</span>}
        {community.win_rate !== null && (
          <span className="t-caption subtle"> ({formatPercent(community.win_rate)})</span>
        )}
      </td>
      <td className="num">
        {community.won_value > 0 ? formatMoney(community.won_value, currency) : <span className="subtle">—</span>}
      </td>
      <td className="num">{formatDuration(community.avg_first_response_seconds)}</td>
      {/* Cohesion is what separates a real cohort from a chain the clustering walked along. */}
      <td className="num tabular">{community.cohesion.toFixed(2)}</td>
    </tr>
  );
}
